import { MainInterfaceProps } from "@components/mainInterface/MainInterface";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_LABELS,
} from "@common/types/reports/report-type";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { FORM_1099_NEC_REPORT_LABEL } from "@components/accounting/form1099Nec/form1099NecFormat";
import {
    isReportingRoute,
    REPORTING_ROUTE,
} from "@components/reporting/reportingRoutes";

export function useAccountingBreadcrumbs() {
    const location = useLocation();
    const { consolidation: isReportingEnabled } = useTypedFlags();

    return useMemo(() => {
        const breadcrumbs: MainInterfaceProps["breadcrumbs"] = [
            isReportingEnabled && isReportingRoute(location.pathname)
                ? { label: "Reporting", to: REPORTING_ROUTE }
                : { label: "Accounting", to: "/accounting" },
        ];

        switch (location.pathname) {
            case "/accounting/profit-loss":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.PROFIT_LOSS
                    ],
                });
                break;
            case "/accounting/balance-sheet":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.BALANCE_SHEET
                    ],
                });
                break;
            case "/accounting/general-ledger":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.GENERAL_LEDGER
                    ],
                });
                break;
            case "/accounting/intercompany-transactions":
            case "/accounting/elimination-detail":
            case "/accounting/elimination-exceptions":
                breadcrumbs.push({
                    label: "Intercompany",
                });
                break;
            case "/accounting/chart-of-accounts":
                breadcrumbs.push({
                    label: "Chart of Accounts",
                });
                break;
            case "/accounting/schedules":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.SCHEDULES
                    ],
                });
                break;
            case "/accounting/trial-balance":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.TRIAL_BALANCE
                    ],
                });
                break;
            case "/accounting/cash-flow-statement":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.CASH_FLOW_STATEMENT
                    ],
                });
                break;
            case "/accounting/reconciliation":
                breadcrumbs.push({
                    label: "Reconciliation",
                });
                break;
            case "/accounting/expenses-by-vendor":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.EXPENSE_BY_VENDOR
                    ],
                });
                break;
            case "/accounting/ar-aging-summary":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.AR_AGING_SUMMARY
                    ],
                });
                break;
            case "/accounting/ar-aging-detail":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.AR_AGING_DETAIL
                    ],
                });
                break;
            case "/accounting/check-detail":
                breadcrumbs.push({
                    label: ACCOUNTING_REPORT_LABELS[
                        AccountingReportType.CHECK_DETAIL
                    ],
                });
                break;
            case "/accounting/1099-nec":
                breadcrumbs.push({ label: FORM_1099_NEC_REPORT_LABEL });
                break;
            default:
                delete breadcrumbs[0].to; // not interactable when there are no breadcrumbs
                break;
        }

        return breadcrumbs;
    }, [isReportingEnabled, location.pathname]);
}
